import React, { useState } from "react";
import _ from "underscore";
import PropTypes from "prop-types";
import useListbox from "../../hooks/use-listbox";
import useSelectRange from "../../hooks/use-select-range";
import Listbox from "./listbox";
import ListboxMultiselectItem from "./listbox-multiselect-item";
import ListboxContent from "./listbox-content";
import ListboxGroup from "./listbox-group";
import ListboxInfoBar from "./listbox-info-bar";
import { TransitionGroup, CSSTransition } from "react-transition-group";

const ListboxMultiselect = ({
  options,
  onChange,
  values = [],
  emptyValue,
  onSearch,
  onClose = () => {},
  scrollFetch,
  loading = false,
  label = "",
  disabled = false,
  showInfoBar = false,
  maxSelections = 1000,
  size,
  icon,
  buttonClassName,
  hideSelectedValue,
  headerText,
  headerClassName = "",
  alwaysShowLabel,
  onOpen = () => {},
}) => {
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [disableShowSelected, setDisableShowSelected] = useState(false);
  const [isMaxSelections, setIsMaxSelections] = useState(false);

  const { filteredOptions, flattenedOptions, handleSearch, handleClose, filteredCount, isGrouped, isSearchable } =
    useListbox(options, onSearch, onClose, scrollFetch);

  const displayOptions = showSelectedOnly ? _.sortBy(values, "value") : filteredOptions;
  const valueCount = values.length;
  const flattenedAndFiltered = isGrouped ? displayOptions.flatMap((option) => option.items) : displayOptions;
  const { getSelections, setLastClicked } = useSelectRange(flattenedAndFiltered, values);
  const getDisplayValue = () => {
    if (valueCount === 0) return "";
    if (valueCount === 1) {
      if (values[0].label) return values[0].label;
      const selected = flattenedOptions.find((option) => option.value === values[0].value);
      if (selected) return selected.label;
      return "";
    }
    return `${label} (${valueCount} selected)`;
  };
  const displayValue = getDisplayValue();
  const labelAndValue = valueCount > 0 ? `${label}. ${displayValue}` : `${label}. No option selected`;
  const handleClick = (clickedOption, isShiftKey, isSelected) => {
    const selections = getSelections(clickedOption, isShiftKey, isSelected);

    if (selections.length > maxSelections) {
      setIsMaxSelections(true);
      setTimeout(() => {
        setIsMaxSelections(false);
      }, 3000);
    } else {
      setIsMaxSelections(false);
      onChange(selections);
    }
  };

  return (
    <Listbox
      ariaLabel={labelAndValue}
      emptyValue={emptyValue}
      value={alwaysShowLabel ? label : displayValue}
      size={size}
      disabled={disabled}
      onClose={() => {
        setShowSelectedOnly(false);
        setLastClicked(null);
        handleClose();
      }}
      icon={icon}
      hideSelectedValue={hideSelectedValue}
      buttonClassName={buttonClassName}
      onOpen={onOpen}
    >
      {({ closePopover }) => (
        <>
          {headerText && <p className={`listbox-select--header ${headerClassName}`}>{headerText}</p>}
          <ListboxContent
            searchable={isSearchable}
            disableSearch={showSelectedOnly}
            filteredCount={filteredCount}
            handleSearch={(val) => {
              setLastClicked(null);
              handleSearch(val);
            }}
            scrollFetch={scrollFetch}
            loading={loading}
            setDisableShowSelected={setDisableShowSelected}
            maxSelections={maxSelections}
            isMaxSelections={isMaxSelections}
          >
            <TransitionGroup>
              {displayOptions.map((option) => {
                return isGrouped ? (
                  <ListboxGroup key={option.label} label={option.label} showLabel={option.items.length > 0}>
                    {option.items.map((item) => (
                      <ListboxMultiselectItem
                        key={item.value}
                        onClick={handleClick}
                        option={item}
                        isSelected={values.some((selected) => selected.value === item.value)}
                      />
                    ))}
                  </ListboxGroup>
                ) : (
                  <CSSTransition key={option.value} timeout={200} classNames="et-listbox--item-transition">
                    <ListboxMultiselectItem
                      onClick={handleClick}
                      option={option}
                      isSelected={values.some((selected) => selected.value === option.value)}
                    />
                  </CSSTransition>
                );
              })}
            </TransitionGroup>
          </ListboxContent>
          {showInfoBar && isSearchable && (
            <ListboxInfoBar
              count={valueCount}
              disableShowSelected={disableShowSelected}
              showSelectedOnly={showSelectedOnly}
              onClear={() => onChange([])}
              onShowSelected={(val) => {
                setShowSelectedOnly(val);
              }}
            />
          )}
        </>
      )}
    </Listbox>
  );
};

const optionsFormat = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    icon: PropTypes.string,
    disabled: PropTypes.bool,
  })
);
const groupedOptionsFormat = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.string.isRequired,
    items: optionsFormat.isRequired,
  })
);

ListboxMultiselect.propTypes = {
  options: PropTypes.oneOfType([optionsFormat, groupedOptionsFormat]).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  /**
   * You can pass an emptyValue if you want to display something other than the default "Select..."
   */
  emptyValue: PropTypes.string,
  onSearch: PropTypes.func,
  onClose: PropTypes.func,
  scrollFetch: PropTypes.func,
  loading: PropTypes.bool,
  values: PropTypes.array,
  disabled: PropTypes.bool,
  icon: PropTypes.string,
  buttonClassName: PropTypes.string,
  hideSelectedValue: PropTypes.bool,
  onOpen: PropTypes.func,
  /**
   * showInfoBar = true will add a toolbar to the bottom of the dropdown with the
   * count of items selected, a clear link to clear all selected items, and
   * "View All | View Selected" links to toggle between all options and only selected options.
   * It is important to note that to display selected options that are not in the first
   * page of results, values must include both the value AND label. Also note that the InfoBar
   * should NOT be used with grouped options.
   */
  showInfoBar: PropTypes.bool,
  maxSelections: PropTypes.number,
  size: PropTypes.string,
  headerText: PropTypes.string,
  headerClassName: PropTypes.string,
  alwaysShowLabel: PropTypes.bool,
};

export default ListboxMultiselect;
